import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axiosInstant from '../api';
import { getProjectExpertScores } from '../api/endpoints';

export interface ProjectScoreProduct {
    desireid: number;
    title: string;
    /** Leaf product_category name (excludes Uncategorized); null if none. */
    category_name?: string | null;
}

export interface AttributeOverlap {
    attrSlug: string;          // 'age-group', 'gender-focus', 'user-tier', etc.
    attrName: string;          // human label
    weight: number;            // parent attribute weight [0..1] — drives tooltip emphasis
    overlap: number;           // Jaccard overlap [0..1]
    expertValues: string[];    // ['Child', 'Infant', 'Teen']
    productValues: string[];   // ['Adult']
    shared: string[];          // intersection
}

export interface AlgoScoreBreakdown {
    /** A — overall weighted attribute alignment (0..1). */
    attributeAlignment: number;
    /** Σ(W·E) — profession relevance × experience (0..1). */
    professionContribution: number;
    /** Per-attribute overlap detail for the three weighted attributes. */
    overlaps: AttributeOverlap[];
}

export interface ExpertProfession {
    professionName: string | null;
    subProfessionName: string | null;
    subSubProfessionName: string | null;
    years: number;
    isActive: boolean;
}

export interface ExpertTypeSignals {
    relevantProfessions: number;
    professionalTier: boolean;
    regulated: boolean;
    bestTenureYears: number;
    minYears: number;
    qualifyingProfessions: Array<{
        name: string | null;
        years: number;
        professionalTier: boolean;
        regulated: boolean;
    }>;
}

/** Industry/Use-Case classification for one expert × product pair. */
export interface ExpertTypeEntry {
    type: 'industry' | 'use-case';
    signals: ExpertTypeSignals;
}

export interface IdealAttributeGroup {
    attrName: string;
    attrSlug: string;
    values: string[];
}

export interface IdealExpertProfession {
    professionName: string | null;
    subProfessionName: string | null;
}

export interface IdealExpert {
    userId: number;
    displayName: string | null;
    /** Persisted algorithm score in [0,1]. */
    score: number;
    professions: IdealExpertProfession[];
}

export interface IdealProfileMember {
    subProfessionName: string | null;
    professionName: string | null;
}

export interface IdealProfile {
    /** One or two sub-professions whose union scores `matchScore` against the product. */
    members: IdealProfileMember[];
    /** Weighted Jaccard of unioned vector × product, [0..1]. */
    matchScore: number;
}

export interface ProductIdeal {
    attributes: IdealAttributeGroup[];
    /** Top N actual verified creators by score for this product. */
    topExperts: IdealExpert[];
    /** Top N hypothetical ideal profiles (1–2 sub-professions) from the taxonomy. */
    topProfiles: IdealProfile[];
}

export interface ProjectExpertScoresResponse {
    products: ProjectScoreProduct[];
    /** Keyed by userId → { desireid: score in [0,1] | null } */
    scoresByUserId: Record<string, Record<string, number | null>>;
    /** Keyed by userId → { desireid: breakdown | null } — drives the tooltip. */
    breakdownByUserId: Record<string, Record<string, AlgoScoreBreakdown | null>>;
    /** Keyed by userId → { desireid: Industry-lens score in [0,1] | null }. */
    industryScoreByUserId: Record<string, Record<string, number | null>>;
    /** Keyed by userId → { desireid: Industry/Use-Case classification | null }. */
    expertTypeByUserId: Record<string, Record<string, ExpertTypeEntry | null>>;
    /** Keyed by userId → true when the expert is Industry for ≥1 project product. */
    isIndustryByUserId: Record<string, boolean>;
    /** Keyed by userId → display name (for the tooltip narrative). */
    displayNameByUserId: Record<string, string>;
    /** Keyed by userId → ordered profession list with tenure. */
    professionsByUserId: Record<string, ExpertProfession[]>;
    /** Keyed by desireid → ideal-expert explanation for that product. */
    productIdealsByDesireId: Record<string, ProductIdeal>;
    isLoading: boolean;
    error: unknown;
    /** Re-run the fetch (e.g. after adding/removing a pro) without remounting. */
    refetch: () => void;
}

/** Internal state shape — everything except the stable `refetch` handle. */
type ScoresState = Omit<ProjectExpertScoresResponse, 'refetch'>;

const EMPTY: ScoresState = {
    products: [],
    scoresByUserId: {},
    breakdownByUserId: {},
    industryScoreByUserId: {},
    expertTypeByUserId: {},
    isIndustryByUserId: {},
    displayNameByUserId: {},
    professionsByUserId: {},
    productIdealsByDesireId: {},
    isLoading: false,
    error: null,
};

/**
 * Fetches the algorithm-generated expert × product scores for a project and
 * exposes them as a fast lookup keyed by userId. The score lives in
 * application/expert_product_scores and is a number in [0, 1]; the UI scales
 * it to a 0–100 % display alongside the human score on each expert card.
 *
 * Skips the fetch when:
 *   - projectId is falsy (page is still loading)
 *   - skip=true (e.g. a public-token page without auth)
 *
 * Caller is expected to look up a given expert's per-product scores via
 *   result.scoresByUserId[String(userid)]?.[String(desireid)]
 */
export function useProjectExpertScores(
    projectId: number | undefined,
    { skip = false }: { skip?: boolean } = {}
): ProjectExpertScoresResponse {
    const [state, setState] = useState<ScoresState>(EMPTY);
    // Monotonic request token: lets a manual refetch (or a projectId change)
    // supersede an older in-flight response instead of racing it.
    const reqIdRef = useRef(0);

    const refetch = useCallback(() => {
        if (skip || !projectId) {
            reqIdRef.current += 1;
            setState(EMPTY);
            return;
        }
        const myReq = (reqIdRef.current += 1);
        setState((s) => ({ ...s, isLoading: true, error: null }));
        axiosInstant
            .get(getProjectExpertScores(projectId))
            .then((res) => {
                if (myReq !== reqIdRef.current) return;
                const products: ProjectScoreProduct[] = res.data?.products || [];
                const experts: Array<{
                    userId: number;
                    displayName?: string;
                    algoScores: Record<string, number | null>;
                    algoBreakdown?: Record<string, AlgoScoreBreakdown | null>;
                    industryScores?: Record<string, number | null>;
                    expertType?: Record<string, ExpertTypeEntry | null>;
                    isIndustryForAny?: boolean;
                    professions?: ExpertProfession[];
                }> = res.data?.experts || [];
                const scoresByUserId: Record<string, Record<string, number | null>> = {};
                const breakdownByUserId: Record<string, Record<string, AlgoScoreBreakdown | null>> = {};
                const industryScoreByUserId: Record<string, Record<string, number | null>> = {};
                const expertTypeByUserId: Record<string, Record<string, ExpertTypeEntry | null>> = {};
                const isIndustryByUserId: Record<string, boolean> = {};
                const displayNameByUserId: Record<string, string> = {};
                const professionsByUserId: Record<string, ExpertProfession[]> = {};
                experts.forEach((e) => {
                    const key = String(e.userId);
                    scoresByUserId[key] = e.algoScores || {};
                    breakdownByUserId[key] = e.algoBreakdown || {};
                    industryScoreByUserId[key] = e.industryScores || {};
                    expertTypeByUserId[key] = e.expertType || {};
                    isIndustryByUserId[key] = !!e.isIndustryForAny;
                    if (e.displayName) displayNameByUserId[key] = e.displayName;
                    professionsByUserId[key] = e.professions || [];
                });
                const rawIdeals: Record<string, ProductIdeal> = res.data?.productIdeals || {};
                const productIdealsByDesireId: Record<string, ProductIdeal> = {};
                Object.keys(rawIdeals).forEach((k) => {
                    productIdealsByDesireId[String(k)] = rawIdeals[k];
                });
                setState({
                    products,
                    scoresByUserId,
                    breakdownByUserId,
                    industryScoreByUserId,
                    expertTypeByUserId,
                    isIndustryByUserId,
                    displayNameByUserId,
                    professionsByUserId,
                    productIdealsByDesireId,
                    isLoading: false,
                    error: null,
                });
            })
            .catch((err) => {
                if (myReq !== reqIdRef.current) return;
                // Non-fatal: the algo score is a nice-to-have on each card. Log
                // and leave the lookup empty so the cards just hide the chips.
                // eslint-disable-next-line no-console
                console.error('useProjectExpertScores failed:', err);
                setState({ ...EMPTY, error: err });
            });
    }, [projectId, skip]);

    useEffect(() => {
        refetch();
        // On unmount / projectId change, invalidate any in-flight response.
        return () => {
            reqIdRef.current += 1;
        };
    }, [refetch]);

    return useMemo(() => ({ ...state, refetch }), [state, refetch]);
}
